import { isEqual } from "lodash-es";
import { useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import type { AppDispatch } from "src/store";

import api from "../../services/api";
import { setFetchCallTranscriptsStatus } from "../../store/calls/store";
import { AssetType } from "../../types/assets";
import { CallState, type TranscriptItem } from "../../types/calls";
import { APIStateEnum } from "../api/states";

export const useAssets = ({
  callId,
  callStatus,
}: {
  callId?: string;
  callStatus: CallState;
}) => {
  const transcriptOffsetRef = useRef(0);
  const [transcriptData, setTranscriptData] = useState<TranscriptItem[]>([]);

  // Reserving null for error scenarios
  const [transcriptID, setTranscriptID] = useState<string | null>("");
  // Reserving null for error scenarios
  const [fullRecordingID, setFullRecordingID] = useState<string | null>("");

  const [downloadedFilePaths, setDownloadedFilePaths] = useState<string[]>([]);
  const dispatch = useDispatch<AppDispatch>();

  // If it is a live call, keep polling until the IDs are available to download
  const pollForAssets = ![
    CallState.ENDED,
    CallState.CANCELED,
    CallState.CANCELLED,
    CallState.COMPLETED,
    CallState.FAILED,
  ].includes(callStatus);
  const pollTimeoutRef = useRef<NodeJS.Timeout>();

  useEffect(() => {
    const fetchAssets = async (
      currentTranscriptID: string | null,
      currentFullRecordingID: string | null,
    ) => {
      const getAssetList = async (assetType: AssetType) => {
        try {
          const response = await api
            .service("dashboard2")
            .get(
              `/api/v1/assets/list/?asset_type=${assetType}&call_id=${callId}`,
            );
          const data = response.data ?? [];
          return data;
        } catch (e) {
          console.error("Error fetching assets:", e);
          return [];
        }
      };

      const requestedAssets = [];
      if (!currentTranscriptID && currentTranscriptID !== null) {
        requestedAssets.push(AssetType.CALL_TRANSCRIPT);
      }
      // Only request recording if we're not polling or don't have a valid ID
      if (
        (!pollForAssets || !currentFullRecordingID) &&
        currentFullRecordingID !== null
      ) {
        requestedAssets.push(AssetType.CALL_FULL_RECORDING);
      }

      // Only make API calls if we need to fetch assets
      if (requestedAssets.length > 0) {
        const [transcriptFiles, fullRecFiles = []] = await Promise.all(
          requestedAssets.map(asset => getAssetList(asset)),
        );

        if (requestedAssets.includes(AssetType.CALL_TRANSCRIPT)) {
          setTranscriptID(
            transcriptFiles?.length > 0 ? transcriptFiles[0].id : null,
          );
        }
        if (requestedAssets.includes(AssetType.CALL_FULL_RECORDING)) {
          setFullRecordingID(
            fullRecFiles?.length > 0 ? fullRecFiles[0].id : null,
          );
        }
      }

      // Only schedule next poll if we're still polling and missing required assets
      if (pollForAssets && (!currentTranscriptID || !currentFullRecordingID)) {
        pollTimeoutRef.current = setTimeout(
          () => fetchAssets(currentTranscriptID, currentFullRecordingID),
          2000,
        );
      }
    };

    if (callStatus) {
      fetchAssets(transcriptID, fullRecordingID);
    }

    return () => {
      // Clear any pending polls
      if (pollTimeoutRef.current) {
        clearTimeout(pollTimeoutRef.current);
      }

      // Delete browser downloaded files on unmount
      downloadedFilePaths.forEach(filePath => {
        URL.revokeObjectURL(filePath);
      });
    };
  }, [pollForAssets, callId, callStatus]); // Removed transcriptID from dependencies

  // Saved to a state as the partials add up
  const getTranscripts = async () => {
    if (transcriptID === null) {
      dispatch(setFetchCallTranscriptsStatus(APIStateEnum.FAILED));
      return;
    }

    if (!transcriptID) {
      return;
    }

    try {
      const offset = transcriptOffsetRef.current || 0;
      const response = await api
        .service("dashboard2", {
          headers: {
            Range: `bytes=${offset}-`,
          },
        })
        .get(`/api/v1/assets/download/${transcriptID}`, {});

      if (response.status !== 200 && response.status !== 206) {
        console.error(
          "Failed to fetch transcript:",
          response.status,
          response.statusText,
        );
        dispatch(setFetchCallTranscriptsStatus(APIStateEnum.FAILED));
        return;
      }

      if (!response.data) {
        console.error("No data in response");
        dispatch(setFetchCallTranscriptsStatus(APIStateEnum.FAILED));
        return;
      }
      // Check if response.data is an object (but not null and not an array) and stringify it if so
      if (
        typeof response.data === "object" &&
        response.data !== null &&
        !Array.isArray(response.data)
      ) {
        response.data = JSON.stringify(response.data);
      }

      const jsonStrings = response.data
        .split("\n")
        .filter((str: string) => str.trim());
      let bytesRead = 0;

      for (const jsonStr of jsonStrings) {
        try {
          bytesRead += new TextEncoder().encode(`${jsonStr}\n`).length;
          const jsonObj = JSON.parse(jsonStr);

          setTranscriptData(prev => {
            const isDuplicate = prev.some(item => isEqual(item, jsonObj));
            return isDuplicate ? prev : [...prev, jsonObj];
          });

          dispatch(setFetchCallTranscriptsStatus(APIStateEnum.SUCCESS));
        } catch (e) {
          console.error("Failed to parse JSON:", e, "String:", jsonStr);
        }
      }

      if (bytesRead > 0) {
        transcriptOffsetRef.current += bytesRead;
      }
    } catch (error) {
      console.error("Error processing transcript:", error);
      dispatch(setFetchCallTranscriptsStatus(APIStateEnum.FAILED));
    }
  };

  // returns local path
  const downloadAndGetAudioFile = async (asset_id: string | null) => {
    if (!asset_id) {
      return null;
    }

    try {
      const response = await api.service("dashboard2").get(
        `/api/v1/assets/download/${asset_id}`,
        {
          headers: {
            Accept: "audio/wav, audio/*",
          },
        },
        { responseType: "arraybuffer" },
      );

      if (response.data instanceof ArrayBuffer) {
        const blob = new Blob([response.data], { type: "audio/wav" });
        const audioUrl = URL.createObjectURL(blob);
        setDownloadedFilePaths(prev => [...prev, audioUrl]);
        return audioUrl;
      }

      console.error("Unexpected response format:", {
        type: typeof response.data,
        isString: typeof response.data === "string",
        firstBytes:
          typeof response.data === "string"
            ? response.data.slice(0, 20)
            : "not a string",
      });

      return null;
    } catch (error: unknown) {
      console.error("Audio download error:", {
        error,
      });
      return null;
    }
  };

  return {
    // Check for these to be present, then call corresponding getters
    transcriptID,
    // Check for these to be present, then call corresponding getters
    fullRecordingID,
    getTranscripts,
    downloadAndGetAudioFile,
    transcriptData,
  };
};
